import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import Moment from 'react-moment';
import moment from 'moment';
import fileExtension from 'file-extension';
import PropTypes from 'prop-types';

import { makeGetCurrentUser, makeGetResolvedProjectUsers } from '../selectors/users';
import { makeGetTask, makeIsDependant } from '../selectors/tasks';
import { makeGetMilestone } from '../selectors/milestones';
import { makeGetTaskNotifications } from '../selectors/notifications';
import { makeGetTaskGroup } from '../selectors/task-groups';
import { makeGetSystemMessagesByTypeAndAssociationId } from '../selectors/messages';
import { makeIsSubscribed } from '../selectors/subscriptions';
import { makeGetProject } from '../selectors/projects';

import { readNotifications } from '../actions/notifications';
import { removeFile } from '../actions/files';
import { fetchTask, updateTask, assignTask, unassignTask, removeTask, moveTask, setTaskVisibility, addTaskDoc } from '../actions/tasks';
import { subscribe, unsubscribe } from '../actions/subscriptions';
import { setCustomFieldValue } from '../actions/custom-fields';
import { openFlyout } from '../actions/flyout';
import { showModal } from '../actions/modal';
import { createRecursionRule, updateRecursionRule, removeRecursionRule } from '../actions/recursion-rules';

import { userRoles } from '../constants';
import { trackEvent } from '../services/events-tracker-service';
import { getUserFriendlyRoles, isTempId, scrollTo } from '../utils/helpers';
import { createSlug, copyUrlToClipboard, createAppUrl } from '../utils';
import { canUser } from '../services/permissions-service';
import { getFile } from '../services/store-service';
import eventBus from '../services/event-bus';
import { createAlert } from '../services/alert-service';
import { canTrackTime } from '../services/plan-service';
import { isFileImage } from '../utils/file';
import { getUserPreference, setUserPreference } from '../services/user-preferences-service';

import RecursionRuleContainer from '../containers/recursion-rule-container';
import DocContainer from '../containers/doc-container';

import DocCard from './doc-card';
import Spinner from './spinner';
import DescriptionPane from './description-pane';
import UsersList from './users-list';
import DateElement from './date-element';
import LabelPicker from './label-picker';
import LabelsList from './labels-list';
import AddUserPopout from './add-user-popout';
import Discussion from './discussion';
import Subtasks from './subtasks';
import MultiUpload from './multi-upload';
import AttachmentThumb from './attachment-thumb';
import SelectMilestonePopout from './select-milestone-popout';
import SelectTaskPopout from './select-task-popout';
import ResourceRepeatPopout from './resource-repeat-popout';
import ResourceRepeatText from './resource-repeat-text';
import CanUser from './can-user';
import FeatureAvailable from './feature-available';
import MilestoneName from './milestone-name';
import TaskName from './task-name';
import DropPanel from './drop-panel';
import Tooltip from './tooltip';
import HelpTooltip from './help-tooltip';
import TimeTrackerCta from './time-tracker-cta';
import TaskNav from './task-nav';
import SubtasksOverview from './subtasks-overview';
import TaskListSelector from './task-list-selector';
import CustomFields from './custom-fields';
import DropdownElement from './dropdown-element';
import CustomFieldsUpgradeNotice from './custom-fields-upgrade-notice';
import HideFrom from './hide-from';
import TaskCompleteToggle from './TaskCompleteToggle';
import TaskArchiveToggle from './TaskArchiveToggle';

import IconUser from '../icons/icon-user';
import IconCalendarSimple from '../icons/icon-calendar-simple';
import IconDiscussionAlt from '../icons/icon-discussion-alt';
import IconArchive from '../icons/icon-archive';
import IconRoadmap from '../icons/icon-roadmap';
import IconTask from '../icons/icon-task';
import IconLink from '../icons/icon-link';
import IconEye from '../icons/icon-eye';
import IconCheck from '../icons/icon-check';
import IconSubtask from '../icons/icon-subtask';
import IconFile from '../icons/icon-file';
import IconLabel from '../icons/icon-label';
import IconCustomField from '../icons/icon-custom-field';
import IconToggle from '../icons/icon-toggle';
import IconBell from '../icons/icon-bell';
import IconArrowDown from '../icons/icon-arrow-down';
import IconArrowLeft from '../icons/icon-arrow-left';
import IconClose from '../icons/icon-close';
import IconRepeat from '../icons/icon-repeat';
import IconNumber from '../icons/icon-number';

const ACTIONS = [
    {
        text: 'View Time Log',
        id: 'viewTimeLog'
    },
    {
        text: 'Move Task',
        id: 'moveTask'
    },
    {
        text: 'Delete Task',
        id: 'remove',
        prompt: {
            title: 'DELETE TASK',
            text: 'Do you really want to delete this task?',
            confirmText: 'Delete',
            confirmClassName: 'btn-caution'
        }
    }
];

const emptyComponent = (
    <div className="discussion-placeholder">
        <div className="message placeholder-with-content">
            <div className="message-side">
                <div className="message-user">
                    <span className="placeholder-particle" />
                </div>
            </div>

            <div className="message-main">
                <p>Don’t let it go unsaid! Post a comment to start a discussion.<br /><strong>@Mention</strong> someone to notify them.</p>
            </div>
        </div>

        <div className="message placeholder-with-content">
            <div className="message-side">
                <div className="message-user">
                    <span className="placeholder-particle" />
                </div>
            </div>

            <div className="message-main">
                <div className="message-head">
                    <h4 className="message-username">
                        <span className="placeholder-particle" />
                    </h4>
                </div>

                <div className="message-body">
                    <span className="placeholder-particle" />
                </div>
            </div>
        </div>
    </div>
);

class TaskSingle extends PureComponent {
    // static whyDidYouRender = true;

    static contextTypes = {
        router: PropTypes.object
    };

    static defaultProps = {
        shouldChangeUrl: true
    };

    state = {
        name: this.props.task ? this.props.task.name : '',
        currentSection: 'subtasks',
        activityVisible: getUserPreference('task_activity_visible') || false,
    };

    handleNameChange = () => {
        this.setState({
            name: this.nameElement.textContent
        });
    }

    handleNameBlur = () => {
        this.editTask({ name: this.nameElement.textContent });
    }

    handleNameKeyDown = event => {
        if (event.which === 13) {
            this.nameElement.blur();
        }
    }
 
    handleNamePaste = e => {
        e.preventDefault();

        const text = (e.originalEvent || e).clipboardData.getData('text/plain');

        document.execCommand('insertHTML', false, text);
    }

    handleAssigneeRemove = userId => {
        unassignTask(this.props.id, [userId]);
    }

    handleAssigneeAdd = user => {
        assignTask(this.props.id, [user.id || user]);
    }

    handleMilestoneSelect = milestone => {
        if (milestone && this.props.task.milestone === milestone.id) {
            return;
        }

        this.updateTask({
            milestone: milestone.id
        });
    }

    handleMilestoneUntie = () => {
        if (!this.props.task.milestone) {
            return;
        }

        this.updateTask({
            milestone: null
        });
    }

    handleTaskSelect = task => {
        if (task && this.props.task.dependency === task.id) {
            return;
        }

        this.updateTask({
            dependency: task.id
        });
    }

    handleTaskUntie = () => {
        if (!this.props.task.dependency) {
            return;
        }

        this.updateTask({
            dependency: null
        });
    }

    handleStoryPointsChange = event => {
        const story_points = parseInt(event.target.value, 10);

        if (isNaN(story_points)) {
            return;
        }

        this.updateTask({ story_points });
    }

    handleArchiveClick = () => {
        trackEvent('TASK.ARCHIVE', {
            via: 'Tasks'
        });

        this.updateTask({
            archived: true
        });

        this.close();
    }

    handleUnarchiveClick = taskGroupId => {
        trackEvent('TASK.UNARCHIVE');

        this.updateTask({
            archived: false,
            task_group: taskGroupId
        });

        this.close();
    }

    handleNewComment = () => {
        setTimeout(() => {
            this.scrollToBottom();
        });
    }

    handleDiscussionFocus = () => {
        // this.scrollToBottom();
    }

    handleDueDateClick = () => {
        this.dueDateCtrl && this.dueDateCtrl.show();
    }

    handleReminderClick = () => {
        this.reminderCtrl && this.reminderCtrl.show();
    }

    handleCopyUrlClick = () => {
        trackEvent('TASK.URL_COPY', {
            via: 'Task'
        });

        copyUrlToClipboard(`${createAppUrl()}/${this.props.task.project}/task/${this.props.id}-${createSlug(this.props.task.name)}`);
    }

    handleDescriptionChange = description => {
        this.editTask({ description });
    }

    handleSubscribeToggleClick = () => {
        if (this.props.subscribed) {
            unsubscribe('tasks', this.props.id);
        } else {
            subscribe('tasks', this.props.id);
        }
    }

    handleTaskReveal = roleIds => {
        setTaskVisibility(this.props.id, true, roleIds);
    }

    handleTaskHide = roleIds => {
        setTaskVisibility(this.props.id, false, roleIds);
    }

    handleToggleActivityClick = () => {
        setUserPreference('task_activity_visible', !this.state.activityVisible);

        this.setState({
            activityVisible: !this.state.activityVisible
        });
    }

    handleAddSubtaskClick = () => {
        this.subtasksCtrl.addSubtask();
    }

    handleUpload = newAttachments => {
        if (!newAttachments) {
            return;
        }

        this.updateTask({
            banner: (this.props.task && this.props.task.banner) ? this.props.task.banner : (this.getBannerFromAttachments(newAttachments) || undefined)
        }, true);

        newAttachments.forEach(file => {
            trackEvent('FILE.UPLOAD', {
                via: 'Task',
                id: file.id,
                fileType: fileExtension(file.name),
                fileSize: file.size
            });
        });
    }

    handleInternalDocSelect = docId => {
        addTaskDoc(this.props.id, docId);
    }

    remove() {
        trackEvent('TASK.REMOVE', {
            via: 'Task'
        });

        removeTask(this.props.id);

        this.close();
    }

    close() {
        if (this.props.onBack) {
            this.props.onBack();
            return;
        }

        this.props.onClose && this.props.onClose();
    }

    getBannerFromAttachments(attachments) {
        if (!attachments) {
            return null;
        }

        const attachment = attachments.find(attachment => isFileImage(attachment.name));
        return attachment ? attachment.url : null;
    }

    handleFileDelete = attachmentId => {
        const file = getFile(attachmentId)
        trackEvent('FILE.DELETE', {
            via: 'Task',
            fileName: file && file.name
        });

        const filteredAttachments = this.props.task.attachments.filter(attachment => attachment && attachment.id !== attachmentId);

        this.updateTask({
            attachments: filteredAttachments,
            banner: this.getBannerFromAttachments(filteredAttachments)
        }, true);

        removeFile(attachmentId);
    }

    handleDueDateChange = nextDate => {
        const nextDateFormatted = nextDate && moment(nextDate).format();

        if (this.props.task.due_date === nextDateFormatted) {
            return;
        }

        if (this.props.milestone) {
            if (new Date(this.props.milestone.start) > nextDate) {
                createAlert('default', 'Task tied to milestone cannot end earlier than milestone start date');
                return;
            }

            if (new Date(this.props.milestone.end) < nextDate) {
                createAlert('default', 'Task tied to milestone cannot end after milestone end date');
                return;
            }
        }

        this.editTask({ due_date: nextDateFormatted });
    }

    handleDueDateRemove = () => {
        this.editTask({ due_date: null });

        this.dueDateCtrl && this.dueDateCtrl.hide();
    }


    handleReminderChange = nextDate => {
        const nextDateFormatted = nextDate && moment(nextDate).format();

        if (this.props.task.reminder === nextDateFormatted) {
            return;
        }

        this.editTask({ reminder: nextDateFormatted });
    }

    handleReminderRemove = () => {
        this.editTask({ reminder: null });

        this.reminderCtrl && this.reminderCtrl.hide();
    }

    handleTaskNavClick = sectionId => {
        this.scrollToSection(sectionId);
    }

    handleScroll = () => {
        if (this.autoscrolling) {
            return;
        }

        const scrollTop = this.scrollElement.scrollTop + this.scrollElement.offsetTop + this.navElement.clientHeight;
        const nodes = Array.from(this.scrollElement.querySelectorAll('.task-pane'));
        const currentSection = nodes[nodes.findIndex(section => section.offsetTop > scrollTop) - 1];
        const sectionId = currentSection && currentSection.getAttribute('data-section');

        if (this.state.currentSection !== sectionId) {
            this.setState({
                currentSection: sectionId
            });
        }   
    }

    handleCommentClick = () => {
        this.scrollToBottom();
    }

    handleTaskMoveClick = () => {
        if (this.props.task.archived || !this.taskMoveToProjectCtrl) {
            return;
        }

        this.taskMoveToProjectCtrl.show();
    }

    handleToggleComplete = (completed, taskGroupId) => {
        this.updateTask({
            completed,
            task_group: taskGroupId
        });

        if (completed) {
            this.close();

            trackEvent('TASK_SINGLE.COMPLETE');
        }
    }

    handleMoveToProjectConfirm = (taskGroupId, projectId) => {
        this.moveToTaskGroup(taskGroupId, projectId);
    }

    handleCustomFieldChange = (id, value) => {
        setCustomFieldValue('task', this.props.id, id, value);
    }

    handleCustomFieldCreateClick = () => {
        this.customFieldsCtrl && this.customFieldsCtrl.create();
    }

    handleActionClick = ({ id }) => {
        switch (id) {
            case 'viewTimeLog':
                this.showTimeLog();
                break;

            case 'moveTask':
                this.taskMoveToProjectCtrl && this.taskMoveToProjectCtrl.show();
                break;

            case 'remove':
                this.remove && this.remove();
                break;

            default:
                return null;
        }
    }

    handleTimeTrackerPlaceholderClick = () => {
        canTrackTime(true);
    }

    handleRepeatTaskUpdate = ({ type, trigger, cron, till }) => {
        if (this.props.task.rule) {
            updateRecursionRule(this.props.task.rule, {
                type,
                trigger,
                cron,
                till
            });

            return;
        }

        createRecursionRule({
            task: this.props.id,
            type,
            trigger,
            cron,
            till
        });
    }

    handleRepeatDisable = () => {
        removeRecursionRule(this.props.task.rule);
    }

    handleUpgradeClick = () => {
        showModal('SWITCH_PLAN');
    }

    showTimeLog() {
        openFlyout('timeTracking', {
            taskId: this.props.id,
            project: this.props.projectId,
            association: 'task'
        });
    }

    moveToTaskGroup(taskGroupId, projectId) {
        if (projectId && projectId !== this.props.task.project) {
            this.updateTask({
                project: projectId,
                labels: [],
                assignees: [],
                milestone: null
            }, true);

            this.context.router.replace(`${this.props.task.project}/task/${this.props.task.id}-${createSlug(this.props.task.name)}`);
        }

        moveTask(this.props.id, {
            type: 'task_group',
            id: taskGroupId
        })
            .then(() => {
                createAlert('success', '<i class="icon material-icons">check_circle</i>  Successfully moved task.');
            });
    }

    scrollToSection(sectionId) {
        this.autoscrolling = true;

        this.scrollTo(this.scrollElement.querySelector(`.section-${sectionId}`).offsetTop - this.scrollElement.offsetTop - this.navElement.clientHeight);

        this.setState({
            currentSection: sectionId
        });

        setTimeout(() => {
            this.autoscrolling = false;
        }, 1500);
    }

    scrollToBottom() {
        if (!this.scrollElement) {
            return;
        }

        this.scrollTo(this.scrollElement.scrollHeight);
    }

    scrollTo(position) {
        scrollTo(this.scrollElement, position);
    }

    taskAttachment(attachment, index) {
        return (
            <AttachmentThumb
                {...attachment}
                key={attachment.id}
                noRedirect={this.props.noRedirect}
                filePreviewPath={`/${this.props.task.project}/task/${this.props.id}/files`}
                association="Task"
                onDelete={this.handleFileDelete}
                siblings={this.props.task.attachments.map(a => a.id)}
            />
        )
    }

    editTask(taskProps) {
        if ('assignees' in taskProps) {
            trackEvent('TASK.USERS_ASSIGN');
        }

        if ('description' in taskProps) {
            trackEvent('TASK.DESCRIPTION_EDIT');
        }

        if ('due_date' in taskProps) {
            if (!this.props.task.due_date) {
                trackEvent('TASK.DEADLINE_ADD');
            } else if (!taskProps.due_date) {
                trackEvent('TASK.DEADLINE_REMOVE');
            } else {
                trackEvent('TASK.DEADLINE_EDIT');
            }
        }

        this.updateTask(taskProps);
    }

    focus() {
        if (this.nameElement) {
            this.nameElement.focus();
        }
    }

    updateTask(taskProps, preventSave = false) {
        return updateTask(this.props.id, taskProps, preventSave);
    }

    markNotificationsAsRead(notifications = []) {
        readNotifications(notifications.map(notification => notification.id));
    }

    attemptFetchTask(force) {
        if (
            !force && (
                !this.props.id ||
                (this.props.task && !(this.props.task.shouldRefetch && !this.props.task.loading))
            )
        ) {
            return;
        }

        fetchTask(this.props.id);
    }

    init() {
        setTimeout(() => {
            eventBus.emitEvent('task:open');
        }, 400);

        // Change url
        if (!this.props.noRedirect && this.props.shouldChangeUrl && this.context.router.getCurrentLocation().pathname === `${this.props.task.project}/task/${this.props.id}`) {
            this.context.router.replace(`${this.props.task.project}/task/${this.props.id}-${createSlug(this.props.task.name)}`);
        }
    }

    setSubtasksCtrl = ctrl => this.subtasksCtrl = ctrl;
    setDiscussionCtrl = ctrl => this.discussionCtrl = ctrl;
    setNavElement = el => this.navElement = el;
    setCustomFieldsCtrl = ctrl => this.customFieldsCtrl = ctrl;

    get discussionElement() {
        return <Discussion
            id={this.props.id}
            type="task"
            inputPlaceholer="Post a comment…"
            dateFormat="ddd, MMM D [at] h:mma"
            preventInitialFocus={true}
            botUseUserName={true}
            smallerSystem={true}
            directUpload={true}
            hideSystem={!this.state.activityVisible}
            utils={['emoji', 'file']}
            users={this.props.projectUsers}
            emptyComponent={emptyComponent}
            provideController={this.setDiscussionCtrl}
            onNewMessage={this.handleNewComment}
            onFocus={this.handleDiscussionFocus}
        />
    }

    get isCompleted() {
        return this.props.task.completed;
    }

    get taskStatusLabel() {
        const { task } = this.props;
        if (task.archived) {
            return 'Archived';
        }

        if (task.completed) {
            return 'Completed';
        }

        if (this.props.taskGroup) {
            return this.props.taskGroup.name;
        }

        return 'Archived';
    }

    componentDidMount() {
        this.props.provideController && this.props.provideController({
            focus: () => {
                this.focus();
            }
        });

        if (this.props.id) {
            this.attemptFetchTask(true);
            this.markNotificationsAsRead(this.props.notifications);
        }

        if (this.props.task && (this.props.task.error || this.props.task.removed)) {
            this.close();
            return;
        }

        if (this.props.task && !this.props.task.loading) {
            this.init()
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.task && (this.props.task.error || this.props.task.removed)) {
            this.close();
            return;
        }

        if (this.props.task && !this.props.task.loading && (!prevProps.task || prevProps.task.loading)) {
            this.init()
        }

        if (this.props.id) {
            if (this.props.id !== prevProps.id) {
                this.attemptFetchTask(true);
            } else {
                this.attemptFetchTask();
            }

            this.markNotificationsAsRead(this.props.notifications);
        }

        if (this.props.task && this.props.task.name !== this.state.name) {
            this.setState({
                name: this.props.task.name
            });
        }
    }

    componentWillUnmount() {
        this.props.provideController && this.props.provideController(null);
    }

    get element() {
        const { task } = this.props;

        if (!task) {
            return <Spinner centered={true} />;
        }

        if (!('name' in task) || task.error) {
            return <Spinner centered={true} />;
        }

        const { dependantOn } = this.props;
        const disabledModules = (this.props.project?.disabled_modules || []);

        return (
            <div className={'content-panel' + (dependantOn ? ' dependant' : '')}>
                <div className="content-panel-main">
                    <div className="content-panel-head">
                        {
                            dependantOn ? (
                                <div className="content-panel-additional-info">
                                    This task is dependent on {dependantOn.type}&nbsp;
                                    <strong>
                                    {
                                        dependantOn.type === 'milestone' ? <MilestoneName id={dependantOn.id} /> : <TaskName id={dependantOn.id} />
                                    }
                                    </strong>
                                    &nbsp;and can't be completed.&nbsp;
                                    <HelpTooltip theme="semi-light">
                                        {
                                            dependantOn.type === 'milestone' ? (
                                                'Tasks tied to a dependent milestone are locked until the tasks tied to the dependency milestone are completed.'
                                            ) : 'Tasks tied to a dependent task are locked until the dependency task is completed.'
                                        }
                                    </HelpTooltip>
                                </div>
                            ) : null
                        }

                        <div className="content-panel-head-top">
                            <div className="content-panel-head-utils">
                                {
                                    this.props.onBack ? (
                                        <button className="content-panel-control btn content-panel-back" onClick={this.props.onBack}><IconArrowLeft size="20px" /></button>
                                    ) : null
                                }

                                {
                                    this.props.task.nice_id ? (
                                        <span className="nice-id-holder">
                                            <Tooltip tooltip="Reference this task in discussions and messages." placement="top">
                                                <span className="nice-id">{ this.props.task.nice_id }</span>
                                            </Tooltip>
                                        </span>
                                    ) : null
                                }

                                <div className="tag-pill action" onClick={this.handleTaskMoveClick}>
                                    { this.taskStatusLabel }

                                    {
                                        !task.archived && !task.completed && canUser('editTask', [task.created_by]) && !dependantOn ? (
                                            <React.Fragment>
                                                <span className="arrow">
                                                    <IconArrowDown size="10px" />
                                                </span>

                                                <TaskListSelector
                                                    title="MOVE TASK"
                                                    projectId={this.props.task.project}
                                                    onSuccess={this.handleMoveToProjectConfirm}
                                                    provideController={ctrl => this.taskMoveToProjectCtrl = ctrl}
                                                />
                                            </React.Fragment>
                                        ) : null
                                    }
                                </div>
                            </div>

                            <div className="content-panel-controls">
                                <div className="content-panel-simple-actions">
                                    <CanUser type="startTimer">
                                        <FeatureAvailable type="timeTracking">
                                            <div className="content-panel-simple-action">
                                                <div className="content-panel-simple-action-inner">
                                                    <TimeTrackerCta key={this.props.id} taskId={this.props.id} projectId={this.props.task.project} iconOnly={true} />
                                                </div>
                                            </div>
                                        </FeatureAvailable>

                                        <FeatureAvailable type="timeTracking" reverse={true}>
                                            <div className="content-panel-simple-action">
                                                <Tooltip
                                                    className="light help-tooltip"
                                                    tooltip={
                                                        <React.Fragment>
                                                            <p><strong><button className="btn btn-link btn-text-warning" onClick={this.handleUpgradeClick}><strong>Upgrade to Business</strong></button> for Time Tracking</strong></p>

                                                            <p>
                                                                Track time spent on individual tasks across projects and reference other user's tracked efforts. <a href="http://help.niftypm.com/en/articles/3059016-time-tracking-reporting" target="_blank" rel="noopener noreferrer">Learn more!</a>
                                                            </p>
                                                        </React.Fragment>
                                                    }
                                                    placement="bottom-start"
                                                >
                                                    <div className="content-panel-simple-action-inner">
                                                        <TimeTrackerCta placeholder={true} />
                                                    </div>
                                                </Tooltip>
                                            </div>
                                        </FeatureAvailable>
                                    </CanUser>

                                    {
                                        !this.props.public ? (
                                            <Tooltip tooltip={this.props.subscribed ? <span>Unsubscribe from task notifications.</span> : <span>Subscribe to get task notifications<br /> when something changes.</span>}>
                                                <div className={'content-panel-simple-action' + (this.props.subscribed ? ' active' : '')} onClick={this.handleSubscribeToggleClick}>
                                                    <div className="content-panel-simple-action-inner">
                                                        <IconBell />
                                                    </div>
                                                </div>
                                            </Tooltip>
                                        ) : null
                                    }

                                    <CanUser type="hideTask">
                                        <div className={'content-panel-simple-action' + (task.hidden?.length ? ' active' : '')}>
                                            <HideFrom
                                                onHide={this.handleTaskHide}
                                                onReveal={this.handleTaskReveal}
                                                hidden={this.props.task.hidden}
                                                creator={this.props.task.created_by}
                                            >
                                                <div>
                                                    <Tooltip tooltip={task.hidden?.length ? <span>Task hidden from {getUserFriendlyRoles(this.props.task.hidden, userRoles())}</span> : <span>Hide task from...</span>}>
                                                        <div className="content-panel-simple-action-inner">
                                                            <IconEye size="16px" />
                                                        </div>
                                                    </Tooltip>
                                                </div>
                                            </HideFrom>
                                        </div>
                                    </CanUser>

                                    {
                                        !this.props.public ? (
                                            <Tooltip tooltip="Copy task URL">
                                                <div className="content-panel-simple-action" onClick={this.handleCopyUrlClick}>
                                                    <div className="content-panel-simple-action-inner">
                                                        <IconLink />
                                                    </div>
                                                </div>
                                            </Tooltip>
                                        ) : null
                                    }

                                    <CanUser type="editTask" customList={[task.created_by]}>
                                        <TaskArchiveToggle
                                            projectId={task.project}
                                            tooltipEnabled={true}
                                            archived={task.archived}
                                            onArchive={this.handleArchiveClick}
                                            onUnarchive={this.handleUnarchiveClick}
                                        >
                                            <div className={'content-panel-simple-action' + (task.archived ? ' active' : '')}>
                                                <div className="content-panel-simple-action-inner">
                                                    <IconArchive />
                                                </div>
                                            </div>
                                        </TaskArchiveToggle>
                                    </CanUser>
                                </div>

                                <CanUser type="removeTask">
                                    <div className="content-panel-control">
                                        <DropdownElement
                                            items={ACTIONS}
                                            defaultXPosition="left"
                                            xAlign="right"
                                            onItemClick={this.handleActionClick}
                                        >
                                            <span className="content-panel-actions-trigger" onClick={this.handleActionsTriggerClick}><i className="material-icons">more_horiz</i></span>
                                        </DropdownElement>
                                    </div>
                                </CanUser>

                                {
                                    this.props.onClose ? (
                                        <button className="content-panel-control btn content-panel-close" onClick={this.props.onClose}><IconClose /></button>
                                    ) : null
                                }
                            </div>
                        </div>

                        <div className="content-panel-title">
                            <CanUser type="editTask" customList={[task.created_by]}>
                                <div className="content-panel-complete-shortcut">
                                    <TaskCompleteToggle
                                        dependantOn={dependantOn}
                                        completed={task.completed}
                                        projectId={task.project}
                                        size="large"
                                        onChange={this.handleToggleComplete}
                                    />
                                </div>
                            </CanUser>

                            <span
                                className={'content-panel-field-input' + ((!this.state.name || this.state.name === '') ? ' empty' : '')}
                                ref={element => {this.nameElement = element}}
                                contentEditable={canUser('editTask', [task.created_by])}
                                placeholder="Add task name…"
                                dangerouslySetInnerHTML={{__html: this.props.task && this.props.task.name}}
                                onInput={this.handleNameChange}
                                onBlur={this.handleNameBlur}
                                onKeyDown={this.handleNameKeyDown}
                                onPaste={this.handleNamePaste}
                            />
                        </div>
                    </div>

                    <div className="content-panel-scrollable nice-scroll" ref={element => this.scrollElement = element} onScroll={this.handleScroll}>
                        <div className="content-panel-block">
                            <DescriptionPane
                                key={this.props.id}
                                editable={canUser('editTask', [task.created_by])}
                                placeholder="Add task description"
                                content={task.description || ''}
                                onChange={this.handleDescriptionChange}
                            />
                        </div>

                        <div className="content-panel-block">
                            <div className="content-panel-utils">
                                {
                                    (!(task.assignees && task.assignees.length) && !canUser('assignToTask', [task.created_by])) ? null : (
                                        <div className="control">
                                            <div className="control-inner">
                                                <div className="control-label">
                                                    <span className="control-icon">
                                                        <IconUser />
                                                    </span>

                                                    <span className="control-text">
                                                        Assignees
                                                    </span>
                                                </div>

                                                <div className="control-value">
                                                    {
                                                        task.assignees && task.assignees.length ? (
                                                            <UsersList
                                                                popoutTitle="ASSIGN TASK TO:"
                                                                projectId={this.props.task.project}
                                                                editable={canUser('assignToTask', [task.created_by])}
                                                                size="small"
                                                                users={task.assignees}
                                                                roles={userRoles()}
                                                                popoutSize="xxsmall"
                                                                subtitles={canUser('unassignFromTask', [task.created_by]) ? 'Un-assign user' : null}
                                                                emailMatchText="Select user role above and click “Invite” below and we’ll shoot this person an email notifying them that they’ve been added to this task."
                                                                noMatchText="Looks like that person isn’t a member yet. Enter their email address to add them to the task and project."
                                                                border={true}
                                                                closeOnEveryClick={true}
                                                                onSubtitleClick={this.handleAssigneeRemove}
                                                                excludedUsers={task.assignees}
                                                                onUserSelect={this.handleAssigneeAdd}
                                                            />
                                                        ) : (
                                                            <React.Fragment>
                                                                <span className="action-text">
                                                                    Add
                                                                </span>

                                                                <AddUserPopout
                                                                    title="ASSIGN TASK TO:"
                                                                    projectId={this.props.task.project}
                                                                    xAlign="left"
                                                                    defaultXPosition="right"
                                                                    size="xxsmall"
                                                                    roles={userRoles()}
                                                                    emailMatchText="Select user role above and click “Invite” below and we’ll shoot this person an email notifying them that they’ve been added to this task."
                                                                    noMatchText="Looks like that person isn’t a member yet. Enter their email address to add them to the task and project."
                                                                    excludedUsers={task.assignees}
                                                                    closeOnEveryClick={true}
                                                                    onUserSelect={this.handleAssigneeAdd}
                                                                >
                                                                    <div className="popout-toggle" />
                                                                </AddUserPopout>
                                                            </React.Fragment>
                                                        )
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    )
                                }

                                {
                                    !(!this.props.project || !disabledModules.includes('milestones')) || (!this.props.task.milestone && !canUser('editTask', [task.created_by])) ? null : (
                                        <div className="control">
                                            <div className="control-inner">
                                                <div className="control-label">
                                                    <span className="control-icon">
                                                        <IconRoadmap />
                                                    </span>

                                                    <span className="control-text">
                                                        Milestone
                                                    </span>
                                                </div>

                                                <div className="control-value">
                                                    {
                                                        this.props.task.milestone ? (
                                                            <MilestoneName id={this.props.task.milestone} />
                                                        ) : (
                                                            <span className="action-text">Tie</span>
                                                        )
                                                    }
                                                </div>

                                                <CanUser type="editTask" customList={[task.created_by]}>
                                                    <SelectMilestonePopout
                                                        title="TIE TASK TO A MILESTONE"
                                                        projectId={this.props.task.project}
                                                        selected={this.props.task.milestone}
                                                        onSelect={this.handleMilestoneSelect}
                                                        onRemove={this.handleMilestoneUntie}
                                                        showCreate={true}
                                                        removeCopy="Untie Milestone"
                                                        xAlign="left"
                                                        defaultXPosition="right"
                                                    >
                                                        <div className="popout-toggle" />
                                                    </SelectMilestonePopout>
                                                </CanUser>
                                            </div>
                                        </div>
                                    )
                                }

                                {
                                    (!this.props.task.dependency && !canUser('editTask', [task.created_by])) ? null : (
                                        <div className="control">
                                            <div className="control-inner">
                                                <div className="control-label">
                                                    <span className="control-icon">
                                                        <IconTask />
                                                    </span>

                                                    <span className="control-text">
                                                        Dependency
                                                    </span>
                                                </div>

                                                <div className="control-value">
                                                    {
                                                        this.props.task.dependency ? (
                                                            <TaskName id={this.props.task.dependency} />
                                                        ) : (
                                                            <span className="action-text">Set</span>
                                                        )
                                                    }
                                                </div>

                                                <CanUser type="editTask" customList={[task.created_by]}>
                                                    <SelectTaskPopout
                                                        title="SET TASK DEPENDENCY"
                                                        excluded={[task.id]}
                                                        projectId={this.props.task.project}
                                                        selected={this.props.task.dependency}
                                                        milestone={this.props.task.milestone}
                                                        onSelect={this.handleTaskSelect}
                                                        onUntie={this.handleTaskUntie}
                                                        xAlign="right"
                                                        defaultXPosition="left"
                                                    >
                                                        <div className="popout-toggle" />
                                                    </SelectTaskPopout>
                                                </CanUser>
                                            </div>
                                        </div>
                                    )
                                }

                                {
                                    (!task.due_date && !canUser('editTask', [task.created_by])) ? null : (
                                        <div className="control">
                                            <div className="control-inner" onClick={this.handleDueDateClick}>
                                                <div className="control-label">
                                                    <span className="control-icon">
                                                        <IconCalendarSimple />
                                                    </span>

                                                    <span className="control-text">
                                                        Due Date
                                                    </span>
                                                </div>

                                                <div className="control-value">
                                                    {
                                                        task.due_date ? (
                                                            <React.Fragment>
                                                                <span className={'control-text nowrap' + (moment().diff(task.due_date) > 0 && !task.completed ? ' text-warning' : '')}>
                                                                    <Moment format="D MMM [at] h:mm A">{task.due_date}</Moment>
                                                                </span>

                                                                <small className="from-now"><Moment fromNow>{task.due_date}</Moment></small>
                                                            </React.Fragment>
                                                        ) : (
                                                            <span className="action-text">
                                                                Add
                                                            </span>
                                                        )
                                                    }

                                                </div>

                                                <CanUser type="editTask" customList={[task.created_by]} key={this.props.id}>
                                                    <DateElement
                                                        selected={task.due_date}
                                                        title={task.due_date ? 'CHANGE DUE DATE' : 'ADD A DUE DATE'}
                                                        xAlign="right"
                                                        defaultXPosition="left"
                                                        removePopoutTitle="REMOVE DUE DATE"
                                                        removeBodyText="Are you sure you want to remove the due date from this task?"
                                                        minDate={moment().startOf('hour').toDate().getTime()}
                                                        showTimeSelect={true}
                                                        showQuickOptions={true}
                                                        onChange={this.handleDueDateChange}
                                                        onRemove={this.handleDueDateRemove}
                                                        provideController={ctrl => this.dueDateCtrl = ctrl}
                                                    />
                                                </CanUser>
                                            </div>
                                        </div>
                                    )
                                }

                                {
                                    (!task.reminder && !canUser('editTask', [task.created_by])) ? null : (
                                        <div className="control">
                                            <div className="control-inner" onClick={this.handleReminderClick}>
                                                <div className="control-label">
                                                    <span className="control-icon">
                                                        <IconCalendarSimple />
                                                    </span>

                                                    <span className="control-text">
                                                        Reminder
                                                    </span>
                                                </div>

                                                <div className="control-value">
                                                    {
                                                        task.reminder ? (
                                                            <React.Fragment>
                                                                <span className={'control-text nowrap' + (moment().diff(task.reminder) > 0 && !task.completed ? ' text-warning' : '')}>
                                                                    <Moment format="D MMM [at] h:mm A">{task.reminder}</Moment>
                                                                </span>

                                                                <small className="from-now"><Moment fromNow>{task.reminder}</Moment></small>
                                                            </React.Fragment>
                                                        ) : (
                                                            <span className="action-text">
                                                                Add
                                                            </span>
                                                        )
                                                    }

                                                </div>

                                                <CanUser type="editTask" customList={[task.created_by]} key={this.props.id}>
                                                    <DateElement
                                                        selected={task.reminder}
                                                        title={task.reminder ? 'CHANGE REMINDER' : 'ADD A REMINDER'}
                                                        xAlign="right"
                                                        defaultXPosition="left"
                                                        removePopoutTitle="REMOVE REMINDER"
                                                        removeBodyText="Are you sure you want to remove the reminder from this task?"
                                                        minDate={moment().startOf('hour').toDate().getTime()}
                                                        showQuickOptions={true}
                                                        showTimeSelect={true}
                                                        onChange={this.handleReminderChange}
                                                        onRemove={this.handleReminderRemove}
                                                        provideController={ctrl => this.reminderCtrl = ctrl}
                                                    />
                                                </CanUser>
                                            </div>
                                        </div>
                                    )
                                }

                                {
                                    !this.props.public ? (
                                        <div className="control">
                                            <div className="control-inner">
                                                <div className="control-label">
                                                    <span className="control-icon">
                                                        <IconRepeat />
                                                    </span>

                                                    <span className="control-text">
                                                        Recurring
                                                    </span>
                                                </div>

                                                <div className="control-value">
                                                    {
                                                        this.props.task.rule ? (
                                                            <RecursionRuleContainer id={this.props.task.rule}>
                                                                <ResourceRepeatText />
                                                            </RecursionRuleContainer>
                                                        ) : (
                                                            <span className="action-text">Set</span>
                                                        )
                                                    }
                                                </div>

                                                <CanUser type="editTask" customList={[task.created_by]}>
                                                    <RecursionRuleContainer id={this.props.task.rule}>
                                                        <ResourceRepeatPopout
                                                            key={this.props.id}
                                                            title="REPEAT THIS TASK"
                                                            projectId={this.props.task.project}
                                                            xAlign="right"
                                                            defaultXPosition="left"
                                                            rule={this.props.task.rule}
                                                            onChange={this.handleRepeatTaskUpdate}
                                                            onDisable={this.handleRepeatDisable}
                                                        >
                                                            <div className="popout-toggle" />
                                                        </ResourceRepeatPopout>
                                                    </RecursionRuleContainer>
                                                </CanUser>
                                            </div>
                                        </div>
                                    ) : null
                                }

                                {
                                    !this.props.public ? (
                                        <div className="control">
                                            <div className="control-inner">
                                                <div className="control-label">
                                                    <span className="control-icon">
                                                        <IconNumber />
                                                    </span>

                                                    <span className="control-text">
                                                        Story Points
                                                    </span>
                                                </div>

                                                <div className="control-value">
                                                    <input
                                                        key={this.props.id}
                                                        className="control-value-input"
                                                        type="text"
                                                        defaultValue={task.story_points}
                                                        disabled={!canUser('editTask', [task.created_by])}
                                                        placeholder={canUser('editTask', [task.created_by]) ? 'Set' : ''}
                                                        onChange={this.handleStoryPointsChange}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ) : null
                                }

                                <div className="control double">
                                    <div className="control-inner">
                                        <div className="control-label">
                                            <span className="control-icon">
                                                <IconLabel />
                                            </span>

                                            <span className="control-text">
                                                Tags
                                            </span>
                                        </div>


                                        <CanUser type="editTask" customList={[task.created_by]}>
                                            <LabelPicker
                                                taskId={this.props.id}
                                                taskLabels={this.props.task.labels || []}
                                                xAlign="left"
                                                defaultXPosition="right"
                                            >
                                                <div className="control-value">
                                                    <LabelsList labels={this.props.task.labels} inverted={true}>
                                                        <span className="action-text">
                                                            Add
                                                        </span>
                                                    </LabelsList>
                                                </div>
                                            </LabelPicker>
                                        </CanUser>

                                        <CanUser type="editTask" customList={[task.created_by]} reverse={true}>
                                            <LabelsList labels={this.props.task.labels} inverted={true} />
                                        </CanUser>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="content-panel-nav-holder" ref={this.setNavElement}>
                            <TaskNav public={this.props.public} onClick={this.handleTaskNavClick} current={this.state.currentSection} />
                        </div>

                        <div className="content-panel-body">
                            <div className="content-panel-body-main">
                                <div className="content-panel-section section-subtasks" data-section="subtasks">
                                    <div className="section-head">
                                        <h4>
                                            <span className="title-icon"><IconSubtask /></span>

                                            <span className="text">Subtasks Progress</span>
                                        </h4>

                                        <SubtasksOverview taskId={this.props.id} />
                                    </div>

                                    <div className="section-body">
                                        <div className="content-panel-subtasks">
                                            <Subtasks
                                                projectId={this.props.task.project}
                                                taskId={this.props.id}
                                                page={this.props.page}
                                                provideController={this.setSubtasksCtrl}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {
                                    !this.props.public ? (
                                        <React.Fragment>
                                            <div className="content-panel-section section-files" data-section="files">
                                                <div className="section-head">
                                                    <h4>
                                                        <span className="title-icon"><IconFile /></span>

                                                        <span className="text">Files</span>

                                                        <MultiUpload
                                                            taskId={this.props.id}
                                                            projectId={this.props.task.project}
                                                            internalDocEnabled={true}
                                                            btnClassName="btn-colored btn-circle btn-xxsmall btn-simple btn-icon"
                                                            onUpload={this.handleUpload}
                                                            onInternalDocSelect={this.handleInternalDocSelect}
                                                        >
                                                            +
                                                        </MultiUpload>
                                                    </h4>
                                                </div>

                                                <div className="section-body">
                                                    <div className="content-panel-files">
                                                        <DropPanel
                                                            taskId={this.props.id}
                                                            upload={true}
                                                            className="drop-panel-default drop-panel-inline drop-panel-small"
                                                            onUpload={this.handleUpload}
                                                        >
                                                            <span><i className="icon ion ion-android-attach" />Drag & drop or <u>choose files</u></span>
                                                        </DropPanel>

                                                        {
                                                            (this.props.task.attachments || []).map((item, index) => this.taskAttachment(item, index))
                                                        }

                                                        {
                                                            (this.props.task.documents || []).map(docId => (
                                                                <DocContainer key={docId} id={docId}>
                                                                    <DocCard />
                                                                </DocContainer>
                                                            ))
                                                        }
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="content-panel-section section-fields" data-section="fields">
                                                <div className="section-head">
                                                    <h4>
                                                        <span className="title-icon"><IconCustomField /></span>

                                                        <span className="text">Custom Fields</span>

                                                        <FeatureAvailable type="customFields">
                                                            <CanUser type="editCustomFields">
                                                                <button className="btn btn-circle btn-xxsmall btn-simple btn-icon btn-colored" onClick={this.handleCustomFieldCreateClick}>+</button>
                                                            </CanUser>
                                                        </FeatureAvailable>
                                                    </h4>
                                                </div>

                                                <div className="section-body">
                                                    <FeatureAvailable type="customFields">
                                                        <CustomFields
                                                            key={this.props.id}
                                                            projectId={this.props.task.project}
                                                            type="row-single"
                                                            association="task"
                                                            associationId={this.props.id}
                                                            values={this.props.task.fields}
                                                            onChange={this.handleCustomFieldChange}
                                                            provideController={this.setCustomFieldsCtrl}
                                                        />
                                                    </FeatureAvailable>

                                                    <FeatureAvailable type="customFields" reverse={true}>
                                                        <CustomFieldsUpgradeNotice />
                                                    </FeatureAvailable>
                                                </div>
                                            </div>

                                            <div className="content-panel-section section-comments" data-section="comments">
                                                <div className="section-head">
                                                    <h4>
                                                        <span className="title-icon"><IconDiscussionAlt /></span>

                                                        <span className="text">{(this.props.task.comments ? this.props.task.comments : '') + ' Comment' + (this.props.task.comments === 1 ? '' : 's')}</span>
                                                    </h4>

                                                    <div className="section-actions">
                                                        <button className="btn btn-small section-action-btn" onClick={this.handleToggleActivityClick}>
                                                            <IconToggle />

                                                            {
                                                                this.state.activityVisible ? 'Hide Activity' : 'Show Activity'
                                                            }
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="section-body">
                                                    {this.discussionElement}
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    ) : null
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        return (
            <div className={'content-panel-holder' + (this.props.id ? ' visible' : '') + (this.props.greaterZIndex ? ' greater-z-index' : '') + (this.props.inside ? ' inside' : '')}>
                { this.element }
            </div>
        )
    }
};

const makeMapStateToProps = () => {
    const getCurrentUser = makeGetCurrentUser();
    const getTask = makeGetTask();
    const getMilestone = makeGetMilestone();
    const isDependant = makeIsDependant();
    const getTaskNotifications = makeGetTaskNotifications();
    const getTaskGroup = makeGetTaskGroup();
    const getSystemMessagesByTypeAndAssociationId = makeGetSystemMessagesByTypeAndAssociationId();
    const isSubscribed = makeIsSubscribed();
    const getProject = makeGetProject();
    const getResolvedProjectUsers = makeGetResolvedProjectUsers();

    return ({ users, projects, taskGroups, tasks, notifications, messages, subscriptions, subtasks, milestones }, ownProps) => {
        const argTaskId = ownProps.id;
        const task =  argTaskId && getTask(tasks.items, argTaskId);
        const taskId = isTempId((task && task.id) || argTaskId) ? null : (task && task.id) || argTaskId;
        const projectId = task && task.project ? task.project : ownProps.projectId;
        const project = getProject(projects.items, projectId);

        return {
            id: taskId,
            currentUser: getCurrentUser(users),
            task,
            projectId,
            project,
            milestone: task && task.milestone && getMilestone(milestones.items, task.milestone),
            dependantOn: isDependant(taskId, true, tasks.items, milestones.items),
            notifications: taskId ? getTaskNotifications(notifications.items, taskId, true) : [],
            taskGroup: task && getTaskGroup(taskGroups.items, task.task_group),
            activities: taskId ? getSystemMessagesByTypeAndAssociationId(messages.items, 'task', taskId) : [],
            subscribed: taskId && isSubscribed(subscriptions, taskId),
            projectUsers: project ? getResolvedProjectUsers(project, users) : []
        }
    }
};

export default connect(makeMapStateToProps)(TaskSingle);
